<template>
  <div class="textarea-container">
    <label v-if="label" :for="id" class="textarea-label">{{ label }}</label>
    <textarea
      :id="id"
      ref="textareaRef"
      :value="modelValue"
      :class="['textarea', { 'has-error': error }]"
      :style="customStyles"
      v-bind="$attrs"
      @input="handleInput"
      @blur="handleBlur"
      @focus="handleFocus"
      @keydown="handleKeydown"
    ></textarea>
    <div v-if="error" class="error-message">{{ error }}</div>
    <div v-if="showCharCount" class="char-count">
      {{ charCount }} / {{ maxLength || '∞' }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextareaComponent',
  
  inheritAttrs: false,
  
  props: [
    'modelValue',
    'label',
    'id',
    'placeholder',
    'disabled',
    'readonly',
    'required',
    'maxLength',
    'minLength',
    'rows',
    'cols',
    'resize',
    'width',
    'height',
    'showCharCount',
    'autoResize',
    'error'
  ],
  
  emits: [
    'update:modelValue',
    'input',
    'blur',
    'focus',
    'keydown',
    'change'
  ],
  
  computed: {
    charCount() {
      return this.modelValue ? this.modelValue.length : 0
    },
    
    customStyles() {
      return {
        resize: this.resize || 'vertical',
        width: this.width || '100%',
        height: this.height || 'auto',
        minHeight: this.autoResize ? 'auto' : undefined
      }
    }
  },
  
  watch: {
    modelValue(newValue) {
      if (this.autoResize) {
        this.$nextTick(() => {
          this.autoResizeTextarea()
        })
      }
    }
  },
  
  mounted() {
    if (this.autoResize) {
      this.autoResizeTextarea()
    }
  },
  
  methods: {
    handleInput(event) {
      const value = event.target.value
      
      if (this.maxLength && value.length > this.maxLength) {
        return
      }
      
      this.$emit('update:modelValue', value)
      this.$emit('input', value, event)
      
      if (this.autoResize) {
        this.autoResizeTextarea()
      }
    },
    
    handleBlur(event) {
      this.$emit('blur', event, this.modelValue)
    },
    
    handleFocus(event) {
      this.$emit('focus', event, this.modelValue)
    },
    
    handleKeydown(event) {
      this.$emit('keydown', event, this.modelValue)
    },
    
    autoResizeTextarea() {
      const textarea = this.$refs.textareaRef
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
      }
    },
    
    focus() {
      this.$refs.textareaRef?.focus()
    },
    
    blur() {
      this.$refs.textareaRef?.blur()
    },
    
    clear() {
      this.$emit('update:modelValue', '')
      this.$emit('input', '')
    }
  }
}
</script>

<style scoped>
.textarea-container {
  width: 100%;
  position: relative;
}

.textarea-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
  font-size: 0.875rem;
}

.textarea {
  width: 100%;
  padding: 0.75rem;
  border: 0.0625rem solid var(--grey-2);
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  min-height: 6.25rem;
  background-color: var(--grey-1);
  color: var(--color-text);
}

.textarea:focus {
  outline: none;
  border-color: var(--grey-0);
}

.textarea:disabled {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.textarea.has-error {
  border: 0.0625rem solid var(--grey-2);
}

.textarea.has-error:focus {
  border: 0.0625rem solid var(--grey-2);
  box-shadow: 0 0 0 0.125rem rgba(220, 53, 69, 0.25);
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.char-count {
  font-size: 0.75rem;
  color: var(--grey-0);
  text-align: right;
  margin-top: 0rem;
  margin-bottom: 0;
}
</style>